import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppContext } from '../../app/Context';
import { classFactory } from '../../db/classes/helpers';
import { courseFactory } from '../../db/courses/helpers';
import { createPollQuestion, getPollDetails, setSlideType } from '../../db/polls';
import { pollFactory } from '../../db/polls/helpers';
import registerInterceptors from '../../db/request-interceptors';
import { PollSlideTypes } from '../../db/types';
import { useClassContext } from '../../modules/class/Context';
import { useCourseContext } from '../../modules/course/Context';
import { usePollContext } from '../../modules/poll/Context';
import { PollParams } from '../../routes/router';
import { enumFromStringValue } from '../../utils/string';
import Logger from '../../utils/logger';

const usePollPage = () => {
  const { courseId, classId, pollId, slideType: slideTypeValues } = useParams<PollParams>();

  const slideType = enumFromStringValue(PollSlideTypes, slideTypeValues);

  const { session, setError, setSession } = useAppContext();

  useEffect(() => {
    if (!courseId) return;
    registerInterceptors({
      token: session?.token ?? null,
      cluster: session?.cluster ?? null,
      universitySlug: session?.university.slug ?? null,
      courseId,
      setError,
      setSession,
    });
  }, [session?.token, session?.cluster, session?.university.slug, courseId, setError]);

  const { setCourse } = useCourseContext();
  const { setClass } = useClassContext();
  const { poll, setPoll } = usePollContext();

  const [isFetchingPollDetails, setIsFetchingPollDetails] = useState(false);

  const fetchDetails = useCallback(async () => {
    try {
      setIsFetchingPollDetails(true);

      if (!session?.userId) return;
      if (!courseId) return;
      if (!classId) return;
      if (!pollId) return;
      if (!slideType) return;

      let {
        courseDetails: apiCourseDetails,
        classDetails: apiClassDetails,
        pollDetails,
        pollSubmissions,
        publishDefaults,
        questionSet,
        questions,
      } = await getPollDetails({
        pollId,
      });

      const courseDetails = courseFactory(apiCourseDetails);
      setCourse(courseDetails);

      const classDetails = classFactory(apiClassDetails);
      setClass(classDetails);

      if (questionSet === 0) {
        questions = await createPollQuestion({ classId, pollId });
        pollDetails.questionSet = 1;
      }

      const { isAddedToPPT } = pollDetails;

      if (isAddedToPPT === 0) {
        await setSlideType({
          classId,
          activityId: pollId,
          slideType,
        });
        pollDetails.isAddedToPPT = 1;
      }

      const poll = pollFactory({
        currentUserId: session?.userId,
        courseId,
        classId,
        pollId,
        apiPoll: pollDetails,
        publishPreferences: publishDefaults,
        apiQuestion: '_id' in questions ? questions : undefined,
        pollSubmissions,
      });

      setPoll(poll);
    } catch (error) {
      Logger.error(error);
    } finally {
      setIsFetchingPollDetails(false);
    }
  }, [session?.userId, courseId, classId, pollId]);

  useEffect(() => {
    if (poll?.id == pollId) return;

    fetchDetails();
  }, [poll?.id, pollId, fetchDetails]);

  return { isFetchingPollDetails };
};

export default usePollPage;
